import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import { useRef, useCallback, useState } from 'react';
import {
  useErrorHandlerContext,
  useUserLanguage
} from '@/customHooks';
import textsData from '@/textData/createSupplierTexts.json'
import { Button, RowSpacer } from '../atoms';
import { useNavigate } from 'react-router';
import { CreateSupplierMutation, UpdateSupplierMutation, useCreateSupplierMutation, UserGenderTypeOption, useUpdateSupplierMutation } from '@/graphql/generated';
import { FetchResult } from '@apollo/client';
import { ActivityType, SupplierDetail, AddressProps, UserPersonalDataProps, BillingDataProps, BankDataProps } from '@/custom-types';
import { AddressFormControlled } from './AddressFormControlled';
import { UserPersonalDataFormControlled } from './UserPersonalDataFormControlled';
import { BillingFormControlled } from './BillingFormControlled';
import { BankFormControlled } from './BankFormControlled';
import { WarnText } from '../atoms/WarnText';

type CreateSupplierFormProps = {
  supplierDetail: SupplierDetail | null
  activity: ActivityType
  readOnly?: boolean
  onSuccess?: (result: FetchResult<CreateSupplierMutation | UpdateSupplierMutation>) => void
}

type SupplierFormData = {
  // User personal data
  firstName: string | null,
  lastName: string | null,
  phoneNumber: string | null,
  phoneNumber2: string | null,
  email: string | null,
  documentId: string | null,
  nationality: string | null,
  genderOption: UserGenderTypeOption | null,
  birthDate: string | null,
  // Address
  street: string | null,
  buildingNumber: string | null,
  addressLine: string | null,
  city: string | null,
  state: string | null,
  country: string | null,
  zipCode: string | null,
  // Bank
  bankAccountName: string | null,
  bankAccountHolder: string | null,
  bankAccountEmail: string | null,
  bankAccountNumber: string | null,
  bankAccountPhone: string | null,
  // Billing
  billingLastName: string | null,
  billingFirstName: string | null,
  billingStreet: string | null,
  billingBuildingNumber: string | null,
  billingAddressLine: string | null,
  billingCity: string | null,
  billingCountry: string | null,
  billingState: string | null,
  billingZipCode: string | null,
  billingWereGoing: string | null,
  billingBic: string | null,
  billingObservation: string | null,
};

const emptyData: SupplierFormData = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  phoneNumber2: '',
  email: '',
  documentId: '',
  nationality: '',
  genderOption: UserGenderTypeOption.Other,
  birthDate: '',
  street: '',
  buildingNumber: '',
  addressLine: '',
  city: '',
  state: '',
  country: '',
  zipCode: '',
  bankAccountName: '',
  bankAccountHolder: '',
  bankAccountEmail: '',
  bankAccountNumber: '',
  bankAccountPhone: '',
  billingLastName: '',
  billingFirstName: '',
  billingStreet: '',
  billingBuildingNumber: '',
  billingAddressLine: '',
  billingCity: '',
  billingCountry: '',
  billingState: '',
  billingZipCode: '',
  billingWereGoing: '',
  billingBic: '',
  billingObservation: '',
};

const StyledForm = styled.form`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 2rem;
`

const SectionWrapper = styled.div`
  border-radius: ${({ theme }) => theme.border.radius.base};
  border-style: solid;
  border-color: ${({ theme }) => theme.colors.border};
  border-width: 1px;
  padding: 2rem;
  margin-top: 1.5rem;
  box-sizing: border-box;
  position: relative;
  margin-bottom: 1.5rem;
  & > span {
    background-color: ${({ theme }) => theme.colors.light};
    position: absolute;
    padding-left: 1rem;
    padding-right: 1rem;
    top: -1.3rem;
    line-height: 0;
  }
`

export const CreateSupplierForm = ({ supplierDetail, activity, readOnly, onSuccess }: CreateSupplierFormProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const supplierFormDataRef = useRef<SupplierFormData>(emptyData)

  const [isWarnTextHidden, setIsWarnTextHidden] = useState(true)

  const navigate = useNavigate()

  const { open: errorHandle } = useErrorHandlerContext()
  
  const [createSupplier, { loading: createSupplierIsLoading }] = useCreateSupplierMutation()
  const [updateSupplier, { loading: updateSupplierIsLoading }] = useUpdateSupplierMutation()

  const onCreateSuccessHandle = (response: FetchResult<CreateSupplierMutation>) => {
    const typeName = response.data?.createSupplier.__typename

    if (typeName === 'SupplierOutput' ) onSuccess?.(response)
    else if (typeName === 'ActionResponseMessageOutput') setIsWarnTextHidden(false)

    return
  }

  const onUpdateSuccessHandle = (response: FetchResult<UpdateSupplierMutation>) => {
    const typeName = response.data?.updateSupplier.__typename

    if (typeName === 'SupplierOutput' ) onSuccess?.(response)
    else if (typeName === 'ActionResponseMessageOutput') setIsWarnTextHidden(false)

    return
  }

  const onChangeUserDataHandle = useCallback((userData: UserPersonalDataProps) => {
    supplierFormDataRef.current.firstName = userData.firstName
    supplierFormDataRef.current.lastName = userData.lastName
    supplierFormDataRef.current.email = userData.email
    supplierFormDataRef.current.phoneNumber = userData.phoneNumber
    supplierFormDataRef.current.phoneNumber2 = userData.secondaryPhoneNumber
    supplierFormDataRef.current.documentId = userData.documentId
    supplierFormDataRef.current.nationality = userData.nationality
    supplierFormDataRef.current.birthDate = userData.birthDate
    supplierFormDataRef.current.genderOption = userData.genderOption
  }, [])

  const onChangeAddressHandle = useCallback((addressData: AddressProps) => {
    supplierFormDataRef.current.street = addressData.street
    supplierFormDataRef.current.buildingNumber = addressData.buildingNumber
    supplierFormDataRef.current.addressLine = addressData.addressLine
    supplierFormDataRef.current.country = addressData.country
    supplierFormDataRef.current.state = addressData.state
    supplierFormDataRef.current.city = addressData.city
    supplierFormDataRef.current.zipCode = addressData.zipCode
  }, [])

  const onChangeBankDataHandle = useCallback((bankData: BankDataProps) => {
    supplierFormDataRef.current.bankAccountName = bankData.name
    supplierFormDataRef.current.bankAccountHolder = bankData.accountHolderName
    supplierFormDataRef.current.bankAccountEmail = bankData.email
    supplierFormDataRef.current.bankAccountNumber = bankData.accountNumber
    supplierFormDataRef.current.bankAccountPhone = bankData.phone
  }, [])

  const onChangeBillingDataHandle = useCallback((billingData: BillingDataProps) => {
    supplierFormDataRef.current.billingStreet = billingData.street
    supplierFormDataRef.current.billingBuildingNumber = billingData.buildingNumber
    supplierFormDataRef.current.billingAddressLine = billingData.addressLine
    supplierFormDataRef.current.billingCountry = billingData.country
    supplierFormDataRef.current.billingState = billingData.state
    supplierFormDataRef.current.billingCity = billingData.city
    supplierFormDataRef.current.billingZipCode = billingData.zipCode
    supplierFormDataRef.current.billingFirstName = billingData.firstName
    supplierFormDataRef.current.billingLastName = billingData.lastName
    supplierFormDataRef.current.billingWereGoing = billingData.wereGoing
    supplierFormDataRef.current.billingBic = billingData.bic
    supplierFormDataRef.current.billingObservation = billingData.observation
  }, [])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    switch (activity) {
      case 'creating':
        createSupplier({ variables: {
          firstName: supplierFormDataRef.current.firstName || '',
          lastName: supplierFormDataRef.current.lastName || '',
          phoneNumber: supplierFormDataRef.current.phoneNumber || '',
          phoneNumber2: supplierFormDataRef.current.phoneNumber2 || '',
          email: supplierFormDataRef.current.email || '',
          documentId: supplierFormDataRef.current.documentId || '',
          nationality: supplierFormDataRef.current.nationality || '',
          genderOption: supplierFormDataRef.current.genderOption || UserGenderTypeOption.Other,
          birthDate: supplierFormDataRef.current.birthDate || '',
          street: supplierFormDataRef.current.street || '',
          buildingNumber: supplierFormDataRef.current.buildingNumber || '',
          addressLine: supplierFormDataRef.current.addressLine || '',
          city: supplierFormDataRef.current.city || '',
          state: supplierFormDataRef.current.state || '',
          country: supplierFormDataRef.current.country || '',
          zipCode: supplierFormDataRef.current.zipCode || '',
          bankAccountName: supplierFormDataRef.current.bankAccountName || '',
          bankAccountHolder: supplierFormDataRef.current.bankAccountHolder || '',
          bankAccountEmail: supplierFormDataRef.current.bankAccountEmail || '',
          bankAccountNumber: supplierFormDataRef.current.bankAccountNumber || '',
          bankAccountPhone: supplierFormDataRef.current.bankAccountPhone || '',
          // Billing fields can be added here if needed by backend
        }})
          .then(onCreateSuccessHandle)
          .catch(errorHandle)
        break

      case 'updating':
        updateSupplier({ variables: {
          id: supplierDetail?.user.id,
          firstName: supplierFormDataRef.current.firstName || undefined,
          lastName: supplierFormDataRef.current.lastName || undefined,
          phoneNumber: supplierFormDataRef.current.phoneNumber || undefined,
          phoneNumber2: supplierFormDataRef.current.phoneNumber2 || undefined,
          email: supplierFormDataRef.current.email || undefined,
          documentId: supplierFormDataRef.current.documentId || undefined,
          nationality: supplierFormDataRef.current.nationality || undefined,
          genderOption: supplierFormDataRef.current.genderOption || undefined,
          birthDate: supplierFormDataRef.current.birthDate || undefined,
          street: supplierFormDataRef.current.street || undefined,
          buildingNumber: supplierFormDataRef.current.buildingNumber || undefined,
          addressLine: supplierFormDataRef.current.addressLine || undefined,
          city: supplierFormDataRef.current.city || undefined,
          state: supplierFormDataRef.current.state || undefined,
          country: supplierFormDataRef.current.country || undefined,
          zipCode: supplierFormDataRef.current.zipCode || undefined,
          bankAccountName: supplierFormDataRef.current.bankAccountName || undefined,
          bankAccountHolder: supplierFormDataRef.current.bankAccountHolder || undefined,
          bankAccountEmail: supplierFormDataRef.current.bankAccountEmail || undefined,
          bankAccountNumber: supplierFormDataRef.current.bankAccountNumber || undefined,
          bankAccountPhone: supplierFormDataRef.current.bankAccountPhone || undefined,
          // Billing fields can be added here if needed by backend
        }})
        .then(onUpdateSuccessHandle)
        .catch(errorHandle)
        break
    }
  }

  return (
    <StyledForm onSubmit={handleSubmit}>
      <SectionWrapper>
        <span><h2>{texts.supplier.title}</h2></span>
        <UserPersonalDataFormControlled
          readOnly={readOnly}
          onChange={onChangeUserDataHandle}
          defaultUserData={{
            firstName: supplierDetail?.firstName || '',
            lastName: supplierDetail?.lastName || '',
            email: supplierDetail?.user.email || '',
            phoneNumber: supplierDetail?.phoneNumber || '',
            secondaryPhoneNumber: supplierDetail?.phoneNumber2 || '',
            documentId: supplierDetail?.documentId || '',
            nationality: supplierDetail?.nationality || '',
            birthDate: supplierDetail?.birthDate || '',
            genderOption: supplierDetail?.genderOption || ''
          }}
        />
      </SectionWrapper>
      <br />
      <SectionWrapper>
        <span><h2>{texts.address.title}</h2></span>
        <AddressFormControlled
          readOnly={readOnly}
          onChange={onChangeAddressHandle}
          defaultAddress={{
            street: supplierDetail?.address.street || '',
            buildingNumber: supplierDetail?.address.buildingNumber || '',
            addressLine: supplierDetail?.address.addressLine || '',
            country: supplierDetail?.address.country?.iso2 || '',
            state: supplierDetail?.address.state?.geonamesAdmin1Code || '',
            city: supplierDetail?.address.city?.geonamesId || '',
            zipCode: supplierDetail?.address.zipCode || '',
          }}
        />
      </SectionWrapper>
      <SectionWrapper>
        <span><h2>{texts.bankInfo.title}</h2></span>
        <BankFormControlled
          readOnly={readOnly}
          onChange={onChangeBankDataHandle}
          defaultData={{
            name: supplierDetail?.bankAccount?.name || '',
            accountHolderName: supplierDetail?.bankAccount?.holderName || '',
            accountNumber: supplierDetail?.bankAccount?.accountNumber || '',
            email: supplierDetail?.bankAccount?.email || '',
            phone: supplierDetail?.bankAccount?.phone || '',
          }}
        />
      </SectionWrapper>
      <br />
      <SectionWrapper>
        <span><h2>{texts.billing.title}</h2></span>
        <BillingFormControlled
          readOnly={readOnly}
          onChange={onChangeBillingDataHandle}
          defaultData={{
            street: '',
            buildingNumber: '',
            addressLine: '',
            country: '',
            state: '',
            city: '',
            zipCode: '',
            firstName: '',
            lastName: '',
            bic: '',
            wereGoing: '',
            observation: '',
          }}
        />
      </SectionWrapper>
      <RowSpacer height={1} />
      <WarnText hidden={isWarnTextHidden} text={texts.warnText} />
      <RowSpacer height={1} />
      <Row justify="center">
        <Col xs="content">
          <Button secondary onClick={() => navigate(-1)}>{texts.supplier.cancelButton}</Button>
        </Col>
        <Col xs="content">
          <Button submit isLoading={createSupplierIsLoading || updateSupplierIsLoading}>{texts.supplier.confirmButton}</Button>
        </Col>
      </Row>
    </StyledForm>
  )
}
